import { Button } from "@/parts/button";
import { Cta } from "@/parts/cta";
import { Footer } from "@/parts/footer";
import { Layout } from "@/parts/layout";
import { Media } from "@/parts/media";
import { Meta } from "@/parts/meta";
import { getClient, urlFor } from "@/utils/client";
import type { GetStaticPropsContext } from "next";
import { dodaysQuery, settingsQuery } from "@/utils/queries";
import { IDoDay, IDoDays, ISettings } from "@/utils/types";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import Image from "next/image";
import Link from "next/link";

// Helper function to get the last day of a month
const getLastDayOfMonth = (monthStr: string, year: number) => {
  const months = {
    januari: 0,
    februari: 1,
    maart: 2,
    april: 3,
    mei: 4,
    juni: 5,
    juli: 6,
    augustus: 7,
    september: 8,
    oktober: 9,
    november: 10,
    december: 11,
  };

  return new Date(year, months[monthStr as keyof typeof months] + 1, 0); // Last day of the month
};

export default function DoDays({
  page,
  dodays,
  settings,
}: {
  page: IDoDays;
  dodays: IDoDay[];
  settings: ISettings;
}) {
  // Assuming dodays.date is in ISO format (YYYY-MM-DD). Adjust parsing as necessary.
  const today = new Date();
  const upcomingDodays = dodays
    .filter((doday) => {
      if (doday.date) {
        return new Date(doday.date) >= today;
      } else if (doday.month && doday.year) {
        const lastDayOfMonth = getLastDayOfMonth(doday.month, doday.year);
        return lastDayOfMonth >= today;
      }
      return false;
    })
    .sort((a, b) => {
      const getComparisonDate = (doday: IDoDay) => {
        if (doday.date) {
          return new Date(doday.date);
        } else if (doday.month && doday.year) {
          return getLastDayOfMonth(doday.month, doday.year);
        }
        return new Date(0);
      };

      const dateA = getComparisonDate(a);
      const dateB = getComparisonDate(b);

      if (
        dateA.getMonth() === dateB.getMonth() &&
        dateA.getFullYear() === dateB.getFullYear()
      ) {
        if (a.date && !b.date) return -1;
        if (!a.date && b.date) return 1;
      }

      return dateA.getTime() - dateB.getTime();
    });
  const pastDodays = dodays.filter(
    (doday) => doday.date && new Date(doday.date) < today,
  );

  // Group upcoming dodays by month and year
  const groupedUpcomingDodays = upcomingDodays.reduce(
    (acc, doday) => {
      const month =
        doday.month || format(new Date(doday.date), "MMMM", { locale: nl });
      const year = doday.year || new Date(doday.date).getFullYear();
      const monthYearKey = `${month} ${year}`;
      if (!acc[monthYearKey]) {
        acc[monthYearKey] = [];
      }
      acc[monthYearKey].push(doday);
      return acc;
    },
    {} as { [key: string]: IDoDay[] },
  );

  return (
    <div className="bg-grey">
      <Meta settings={settings} data={page.meta} />
      <div className="">
        <div className="flex flex-col items-center gap-16 md:gap-32 component-padding py-32 text-darkGrey text-center">
          <div className="flex flex-col component-gap max-w-5xl">
            <p className="heading">{page.title}</p>
            <p className="">{page.description}</p>
          </div>
          {upcomingDodays?.length > 0 && (
            <div className="w-full grid grid-cols-1 items-end component-gap">
              {Object.keys(groupedUpcomingDodays)
                .sort((a, b) => {
                  // Sort by year first, then by month
                  const getYear = (key: string) => {
                    const yearMatch = key.match(/\d{4}/);
                    return yearMatch ? parseInt(yearMatch[0]) : 0;
                  };
                  const getMonthNum = (key: string) => {
                    const months: { [key: string]: number } = {
                      januari: 1,
                      februari: 2,
                      maart: 3,
                      april: 4,
                      mei: 5,
                      juni: 6,
                      juli: 7,
                      augustus: 8,
                      september: 9,
                      oktober: 10,
                      november: 11,
                      december: 12,
                    };
                    const monthName = key.split(" ")[0];
                    return months[monthName] || 0;
                  };
                  const yearA = getYear(a);
                  const yearB = getYear(b);
                  if (yearA !== yearB) {
                    return yearA - yearB;
                  }
                  return getMonthNum(a) - getMonthNum(b);
                })
                .map((monthYear, idx) => (
                  <div className="flex flex-col gap-8" key={idx}>
                    <h2 className="z-20 text-2xl font-bold mb-4 text-left sticky top-[80px] bg-grey border-b border-darkGrey/20 pb-4 pt-4 !mb-8">
                      {monthYear}
                    </h2>
                    {groupedUpcomingDodays[monthYear].map((doday, i) => {
                      return (
                        <div
                          key={i}
                          className=" bg-white !no-underline grid lg:grid-cols-2 "
                        >
                          {doday.thumbnail?.image ? (
                            <Image
                              className="aspect-[4/3] h-full w-full object-cover "
                              width={500}
                              height={500}
                              alt={doday.thumbnail?.caption ?? ""}
                              src={urlFor(doday.thumbnail.image).url()}
                            />
                          ) : (
                            <div className="aspect-[4/3] h-full w-full bg-gray-200" />
                          )}
                          <div className="flex flex-col p-8 justify-center gap-4 text-left">
                            <div className="flex flex-col md:flex-row gap-4">
                              <p className="flex items-center gap-2">
                                <Image
                                  src={"/date.svg"}
                                  width={24}
                                  height={24}
                                  alt="date"
                                />
                                {doday.month && doday.year
                                  ? `${doday.month} ${doday.year}`
                                  : format(
                                      new Date(doday.date),
                                      "d MMMM yyyy",
                                      {
                                        locale: nl,
                                      },
                                    )}
                              </p>
                              <p className="flex items-center gap-2">
                                <Image
                                  src={"/time.svg"}
                                  width={24}
                                  height={24}
                                  alt="time"
                                />
                                {doday.timeOverride
                                  ? doday.timeOverride
                                  : doday.month && doday.year
                                    ? `${doday.month} ${doday.year}`
                                    : `${format(new Date(doday.date), "HH:mm", {
                                        locale: nl,
                                      })} - ${format(
                                        new Date(
                                          doday.endDate
                                            ? doday.endDate
                                            : doday.date,
                                        ),
                                        "HH:mm",
                                        { locale: nl },
                                      )}`}
                              </p>
                            </div>
                            <p className="flex items-center gap-2">
                              <Image
                                src={"/location.svg"}
                                width={24}
                                height={24}
                                alt="location"
                              />
                              {doday.location}
                            </p>

                            <p className="font-display text-left text-xl md:text-2xl">
                              {doday.name}
                            </p>
                            <p>{doday.description}</p>
                            <div className="flex gap-4">
                              {doday.applicationLink ? (
                                <Button
                                  label="Aanmelden"
                                  link={doday.applicationLink}
                                />
                              ) : (
                                <p className="italic opacity-75">
                                  Aanmelden nog niet mogelijk
                                </p>
                              )}

                              {doday.layout?.parts &&
                                doday.enableDetailPage &&
                                doday.slug?.current && (
                                  <Link
                                    className="flex items-center gap-2 italic"
                                    key={i}
                                    href={`/doe-dagen/${doday.slug.current}`}
                                  >
                                    Lees meer
                                    <svg
                                      width="10"
                                      height="11"
                                      viewBox="0 0 10 11"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M7.24358 6.16363H0.214722V4.83655H7.24358L4.05364 1.6466L4.99997 0.714844L9.78521 5.50009L4.99997 10.2853L4.05364 9.35357L7.24358 6.16363Z"
                                        fill="#000"
                                      />
                                    </svg>
                                  </Link>
                                )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
            </div>
          )}
          {page.gallery && (
            <Media
              _type="media"
              title={page.galleryTitle}
              description=""
              gallery={page.gallery}
            />
          )}
          {page.layout?.parts?.length > 0 && <Layout data={page.layout} />}
          {/* <div className="flex flex-col component-gap max-w-5xl mt-16">
            <p className="heading">Archief</p>
          </div>
          <div className="w-full">
            {pastDodays
              .sort(
                (a, b) =>
                  new Date(b.date).getTime() - new Date(a.date).getTime()
              )
              .map((doday, i) => {
                return (
                  // <Link key={i} href={`/doe-dagen/${doday.slug.current}`} className=" !no-underline">
                  <div
                    key={i}
                    className="transition-all  bg-white grid grid-cols-1 md:grid-cols-3 w-full p-8"
                  >
                    <p className="font-display text-left text-xl md:justify-self-start">
                      {doday.name}
                    </p>
                    <p className="">{doday.location}</p>
                    <p className="md:justify-self-end">
                      {format(new Date(doday.date), "d MMMM yyyy", {
                        locale: nl,
                      })}
                    </p>
                  </div>
                  // </Link>
                );
              })}
          </div> */}
        </div>
      </div>
      {page.cta && <Cta {...page.cta} />}
      <Footer data={settings} />
    </div>
  );
}

export async function getStaticProps(ctx: GetStaticPropsContext) {
  const draftMode = ctx.draftMode ?? false;
  const client = getClient(draftMode);
  const page = await client.fetch(`*[_type == "dodays"][0]{
        ...,
        layout {
          ...,
          parts[]{
            ...,
            gallery{
              ...,
              items[]{
                ...,
                "playbackId": video.asset->playbackId
              }
            },
            "members": select(
              memberSource == "allTeamMembers" => *[_type == "teamMember"]{..., "videoPlaybackId": video.asset->playbackId},
              memberSource == "allMembers" => *[_type == "member"]{..., "videoPlaybackId": video.asset->playbackId},
              memberSource == "allTestimonials" => *[_type == "testimonial"]{..., "title": name, "quote": coalesce(headline, quote), "description": quote, "role": coalesce(role, company), "partnerLogo": partner->logo, "partnerLogoLight": partner->lightLogo, "videoPlaybackId": video.asset->playbackId},
              members[]->{..., "title": coalesce(title, name), "quote": coalesce(headline, quote), "description": coalesce(description, quote), "role": coalesce(role, company), "partnerLogo": partner->logo, "partnerLogoLight": partner->lightLogo, "videoPlaybackId": video.asset->playbackId}
            ),
            "partners": select(allPartners == true => *[_type == "partner"], partners[]->),
            _type == 'testimonials' => {
              ...,
              "testimonials": testimonials[]->{
                name,
                role,
                company,
                quote,
                image,
                button
              }
            },
            _type == 'stats' => {
              ...,
              "items": select(
                allStats == true => *[_type == "stat"] | order(order asc) {
                  ...,
                  media[]{
                    ...,
                    "playbackId": video.asset->playbackId
                  }
                },
                items[]->{
                  ...,
                  media[]{
                    ...,
                    "playbackId": video.asset->playbackId
                  }
                }
              ),
              "partners": *[_type == "partner"],
            },
            _type == 'knowledge' => {
              ...,
              highlights[]{
                ...,
                "fileURL": file.asset->url
              },
              items[]{
                ...,
                items[]{
                  ...,
                  "fileURL": file.asset->url
                }
              }
            },
            _type == 'faqs' => {
              ...,
              "collectionQuestions": *[_type == "faqItem" && count(audience[@ in ^.^.audience]) > 0]{
                question,
                answer
              },
              "pickedQuestions": items[]->{question, answer}
            },
            _type == 'blogPosts' => {
              ...,
              "blogs": *[_type == "blogPost" && ^.tag in tags] | order(date desc)
            }
          }
        }
      }`);
  const settings = await client.fetch(settingsQuery);

  const dodays = await client.fetch(dodaysQuery);

  return {
    props: {
      page,
      settings,
      dodays,
      draftMode,
    },
    revalidate: 5,
  };
}
